import Page from '../../../../components/PageWrapper';
import { useEffect, useState } from 'react';
import GTable from '../../../../components/elements/GTable';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useModal from '../../../../hooks/modalHook';
import { capitalizeSentence, formatCurrencyAmount, formatNumberValue } from '../../../../utils/miscFunctions';
import useRoute from '../../../../hooks/routesHook';
import { AlertCircle, Building, CheckCircle2, Clock, FileText } from 'lucide-react';
import usePartner from '../../../../hooks/partnerHook';
import useUserAccount from '../../../../hooks/userAccountHook';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import { Company } from '../../../../models/company/Company';
import { COMPANY_TYPES } from '../../../../resources/enums';
import useBills from '../../../../hooks/billsHook';


const Bills = (): JSX.Element => {
    const page = 'bill';
    const { user } = useUserAccount();
    const { ModalComponent } = useModal();
    const { fetchAllCompaniesByTypes } = usePartner();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();

    const { data: companies } = useQuery({
        queryKey: ['companies'],
        queryFn: () => fetchAllCompaniesByTypes([COMPANY_TYPES.BROKER, COMPANY_TYPES.INSURER]),
        enabled: !!user?.isOwner() || !!user?.isInternalSupervisor()
    });

    const companyId = !user?.isInternalAdmin() ? user?.company?.id : companies?.[0]?.id ?? ''
    const [selectedCompany, setSelectedCompany] = useState<any>(companyId);

    useEffect(() => {
        setSelectedCompany(companyId);
    }, [companyId]);

    const { currentPage, fetchAllBills, fetchBillStatisticsByCompanyId, columns, billsPaginationData, loading, handlePageChange, showPerPage, setShowPerPage, BillsModalComponent } = useBills({ selectedCompany });
    
    const { data: billStatistics } = useQuery({
        queryKey: ['billStatistics', selectedCompany],
        queryFn: fetchBillStatisticsByCompanyId,
        enabled: !!selectedCompany
    });

    const statistics = [
        {
            title: 'Total Bills',
            amount: billStatistics?.totalAmountPayable,
            count: billStatistics?.totalBills,
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            textColor: 'text-blue-600 dark:text-blue-300',
            icon: <FileText className="h-6 w-6 text-blue-600 dark:text-blue-300" />,
            due: billStatistics?.totalAmountDue,
            discount: billStatistics?.totalDiscount,
            billsCount: billStatistics?.totalBills,
            totalBills: billStatistics?.totalBills
        },
        {
            title: 'Pending Bills',
            amount: billStatistics?.unpaidAmountPayable,
            count: billStatistics?.unpaidBills,
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
            textColor: 'text-yellow-600 dark:text-yellow-300',
            icon: <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />,
            due: billStatistics?.unpaidAmountDue,
            discount: billStatistics?.unpaidDiscount,
            billsCount: billStatistics?.unpaidBills,
            totalBills: billStatistics?.totalBills
        },
        {
            title: 'Overdue Bills',
            amount: billStatistics?.overdueAmountPayable,
            count: billStatistics?.overdueBills,
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            textColor: 'text-red-600 dark:text-red-300',
            icon: <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-300" />,
            due: billStatistics?.overdueAmountDue,
            discount: billStatistics?.overdueDiscount,
            billsCount: billStatistics?.overdueBills,
            totalBills: billStatistics?.totalBills
        },
        {
            title: 'Paid Bills',
            amount: billStatistics?.paidAmount,
            count: billStatistics?.paidBills,
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            textColor: 'text-green-600 dark:text-green-300',
            icon: <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-300" />,
            due: 0,
            discount: 0,
            billsCount: billStatistics?.paidBills,
            totalBills: billStatistics?.totalBills
        }
    ]

    const { data: bills, isLoading } = useQuery({
        queryKey: ['bills', currentPage, showPerPage, selectedCompany],
        queryFn: fetchAllBills
    });


    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="mb-3">
                        <div className="inline-flex items-center gap-2 mb-2">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
                        </div>

                        <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                            View a list of all {page}s and their details.
                        </p>
                    </div>


                    {((user?.isInternalOwner() || user?.isInternalSupervisor()) && companies && companies?.length > 0) && (
                        <div className="">
                            {/* Broker Selector */}
                            <SelectDropdown
                                list={companies?.map((company: Company) => ({
                                    name: company.name,
                                    value: company.id,
                                }))}
                                placeholder='Select Company'
                                listSelectedValue={selectedCompany}
                                selectInputClassName='!py-[6px]'
                                prepend={<Building size={16} className="text-gray-500 dark:text-darkBg-subText mr-2" />}
                                onChange={(value: string) => setSelectedCompany(Number(value))}
                            />
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    {statistics.map((statistic: any) => (
                        <StatisticsCard
                            key={statistic.title}
                            amount={statistic.amount}
                            title={statistic.title}
                            count={statistic.count}
                            bgColor={statistic.bgColor}
                            textColor={statistic.textColor}
                            icon={statistic.icon}
                            due={statistic.due}
                            discount={statistic.discount}
                            billsCount={statistic.billsCount}
                            totalBills={statistic.totalBills}
                        />
                    ))}
                </div>

                {/* Table */}
                <div className="overflow-x-auto mt-4">
                    <GTable
                        columns={columns}
                        loading={isLoading}
                        data={bills || []}
                        classes="h-fit"
                        showSearch={false}
                        tableType={'bill'}
                        paginationData={billsPaginationData}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        stickyHeader
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                        onRowClick={(row: any) => navigate(`${getDashboardBase()}/bills/${row.id}`)}
                    />
                </div>
            </div>
            <BillsModalComponent />
            <ModalComponent />
        </Page>
    );
};

export default Bills;



const StatisticsCard = ({
    title,
    amount,
    count,
    bgColor,
    textColor,
    icon,
    due,
    discount,
    billsCount,
    totalBills
}: StatisticsCardProps) => {
    return (
        <div className="bg-white dark:bg-darkBg dark:border dark:border-darkBg-border rounded-2xl p-4 shadow">
            <div className="flex items-center mb-3">
                <div className={`rounded-full ${bgColor} p-3 mr-4`}>
                    {icon}
                </div>
                <div>
                    <p className="text-sm text-gray-500 dark:text-darkBg-subText">{title}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-darkBg-mainText">
                        {formatCurrencyAmount(amount)}
                    </p>
                </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-darkBg-border space-y-1">
                <div className="flex justify-between text-xs">
                    <span className="text-gray-600 dark:text-darkBg-subText">Due:</span>
                    <span className="font-semibold text-gray-900 dark:text-darkBg-mainText">
                        {formatCurrencyAmount(due)}
                    </span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-gray-600 dark:text-darkBg-subText">Discount:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                        {formatCurrencyAmount(discount)}
                    </span>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t border-gray-100">
                    <span className="text-gray-600 dark:text-darkBg-subText">Bills:</span>
                    <span className={`font-bold ${textColor}`}>
                        {billsCount || 0} out of {totalBills || 0} {totalBills ? (Number(totalBills) < 2 ? 'bill' : 'bills') : 'bill'}
                    </span>
                </div>
            </div>
        </div>
    );
};

type StatisticsCardProps = {
    title: string,
    count: number,
    amount: number,
    bgColor: string,
    textColor: string,
    icon: JSX.Element,
    due?: number,
    discount?: number,
    billsCount?: number,
    totalBills?: number
}